import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "@/components/AuthProvider";
import { googleSignIn } from "@/pages/Dashboard";

const LINKS = [
  { label: "Courses", href: "#courses", id: "courses" },
  { label: "Why Us", href: "#why", id: "why" },
  { label: "Mentor", href: "#mentor", id: "mentor" },
  { label: "Live", href: "/live", id: "live", route: true },
  { label: "Contact", href: "#contact", id: "contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${scrolled ? "border-white/10 bg-black/60 backdrop-blur-xl" : "border-transparent bg-transparent"}`}>
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-12">
        <a href="/" data-testid="header-logo" className="font-display text-xl font-black tracking-tighter">
          PP<span className="text-brand">.</span>ACADEMY
        </a>
        <nav className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) =>
            l.route ? (
              <Link key={l.id} to={l.href} data-testid={`nav-${l.id}-link`} className="link-underline text-sm font-medium uppercase tracking-widest text-zinc-400 hover:text-white">
                {l.label}
              </Link>
            ) : (
              <button key={l.id} data-testid={`nav-${l.id}-link`} onClick={() => go(l.href)} className="link-underline text-sm font-medium uppercase tracking-widest text-zinc-400 hover:text-white">
                {l.label}
              </button>
            )
          )}
        </nav>
        <div className="flex items-center gap-4">
          {user ? (
            <Link
              to="/dashboard"
              data-testid="header-account-link"
              className="hidden items-center gap-2.5 border border-white/15 py-2 pl-2 pr-4 transition-colors duration-300 hover:border-brand md:flex"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="h-6 w-6 rounded-full" referrerPolicy="no-referrer" />
              ) : (
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand/20"><User size={13} className="text-brand" /></span>
              )}
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">My Courses</span>
            </Link>
          ) : (
            <button
              data-testid="header-signin-button"
              onClick={googleSignIn}
              className="hidden border border-white/15 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-300 hover:border-brand hover:text-white md:block"
            >
              Sign in
            </button>
          )}
          <button
            data-testid="header-enroll-button"
            onClick={() => go("#courses")}
            className="hidden bg-brand px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 focus-visible:ring-2 focus-visible:ring-brand md:block"
          >
            Enrol Now
          </button>
          <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} className="text-white md:hidden" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-6 px-6 py-8">
              {LINKS.map((l) =>
                l.route ? (
                  <Link key={l.id} to={l.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.id}-link`} className="text-left font-display text-2xl font-bold uppercase tracking-tight text-zinc-300">
                    {l.label}
                  </Link>
                ) : (
                  <button key={l.id} data-testid={`mobile-nav-${l.id}-link`} onClick={() => go(l.href)} className="text-left font-display text-2xl font-bold uppercase tracking-tight text-zinc-300">
                    {l.label}
                  </button>
                )
              )}
              {user ? (
                <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="mobile-nav-account-link" className="text-left font-display text-2xl font-bold uppercase tracking-tight text-brand">
                  My Courses
                </Link>
              ) : (
                <button data-testid="mobile-nav-signin-button" onClick={googleSignIn} className="text-left font-display text-2xl font-bold uppercase tracking-tight text-brand">
                  Sign in
                </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
